import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  SwapHoriz as SwitchIcon,
  Person as PersonIcon,
  SupportAgent as AgentIcon,
  AdminPanelSettings as AdminIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'customer' | 'agent' | 'admin';
  avatar?: string;
}

interface ProfileSwitcherProps {
  currentUser: User;
  onProfileSwitch: (newRole: 'customer' | 'agent' | 'admin') => void;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ currentUser, onProfileSwitch }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSwitch = (newRole: 'customer' | 'agent' | 'admin') => {
    onProfileSwitch(newRole);
    handleClose();
  };

  const profiles = [
    {
      role: 'customer' as const,
      title: 'Customer Experience',
      icon: <PersonIcon />,
      color: '#1fb8cc',
      description: 'Customer support and services',
    },
    {
      role: 'agent' as const,
      title: 'Support Agent',
      icon: <AgentIcon />,
      color: '#95c100',
      description: 'Customer service agent tools',
    },
    {
      role: 'admin' as const,
      title: 'Administrator',
      icon: <AdminIcon />,
      color: '#1e212a',
      description: 'Full platform administration',
    },
  ];

  const currentProfile = profiles.find(p => p.role === currentUser.role);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<SwitchIcon />}
        endIcon={<ArrowDownIcon />}
        sx={{
          textTransform: 'none',
          borderColor: currentProfile?.color,
          color: currentProfile?.color,
          '&:hover': {
            borderColor: currentProfile?.color,
            backgroundColor: `${currentProfile?.color}15`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={currentUser.avatar}
            sx={{ width: 24, height: 24 }}
          />
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1 }}>
              {currentUser.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
              {currentProfile?.title}
            </Typography>
          </Box>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Switch Demo Profile
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Experience different user perspectives
          </Typography>
        </Box>
        
        <Divider />

        {profiles.map((profile) => (
          <MenuItem
            key={profile.role}
            onClick={() => handleProfileSwitch(profile.role)}
            disabled={profile.role === currentUser.role}
            sx={{
              py: 1.5,
              px: 2,
              '&:hover': {
                backgroundColor: `${profile.color}08`,
              },
            }}
          >
            <ListItemIcon sx={{ color: profile.color }}>
              {profile.icon}
            </ListItemIcon>
            <ListItemText>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {profile.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {profile.description}
                  </Typography>
                </Box>
                {profile.role === currentUser.role && (
                  <Chip
                    label="Current"
                    size="small"
                    sx={{
                      backgroundColor: profile.color,
                      color: 'white',
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                )}
              </Box>
            </ListItemText>
          </MenuItem>
        ))}

        <Divider sx={{ mt: 1 }} />
        
        <Box sx={{ p: 2, pt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            💡 Profile switching generates new random demo data
          </Typography>
        </Box>
      </Menu>
    </>
  );
};

export default ProfileSwitcher; 